import { useEffect, useState, useCallback } from 'react';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

import { UserRole } from '@newModels';
import { mainPageShowedBannersKey, closedLasReviewRequest, mainPageIsBannerShownKey } from '@constants';
import { BannerPlacement } from '@hook/useAnimatedAppsBanner';
import { AnimatedAppsPromotionBanner } from '@components/AnimatedAppsPromotionBanner';
import { AnimatedSmartBanner } from '@components/AnimatedSmartBanner';
import { AnimatedReviewBanner } from '@components/AnimatedReviewBanner';

import { useMainPageAnimatedBannerV4Query } from './gql/graphql.v4.generated';
import { useMainPageAnimatedBannerQuery } from './gql/graphql.v3.generated';

dayjs.extend(duration);

enum Banner {
  ReviewRequest = 'reviewRequest',
  Apps = 'apps',
  Smart = 'smart',
}

type StorageData = Partial<Record<Banner, string>>;

const BANNERS_LIST = [
  Banner.ReviewRequest,
  Banner.Apps,
  Banner.Smart,
];

const getShowedBanners = () => {
  const data = localStorage.getItem(mainPageShowedBannersKey);
  return data ? JSON.parse(data) as StorageData : null;
};

const setShowedBanners = (data: StorageData) => {
  localStorage.setItem(mainPageShowedBannersKey, JSON.stringify(data));
};

const getIsBannerShown = () => !!sessionStorage.getItem(mainPageIsBannerShownKey);

const setIsBannerShown = (isShown: boolean) => {
  sessionStorage.setItem(mainPageIsBannerShownKey, String(isShown));
};

const setClosedLastReviewRequest = (
  requestId: number,
) => localStorage.setItem(closedLasReviewRequest, requestId.toString());

const getClosedLastReviewRequest = () => Number(localStorage.getItem(closedLasReviewRequest));

export const MainPageAnimatedBannerContainer = () => {
  const [visibleBanner, setVisibleBanner] = useState<Banner | null>(null);

  const {
    data: bannerData,
    loading: bannerDataLoading,
  } = useMainPageAnimatedBannerQuery({
    context: {
      batch: true,
      ignoreAuthError: true,
    },
  });

  const {
    data: bannerDataV4,
    loading: bannerDataV4Loading,
  } = useMainPageAnimatedBannerV4Query({
    context: {
      v4: true,
      batch: true,
      ignoreAuthError: true,
    },
  });

  const reviewRequest = bannerDataV4?.viewer?.reviewRequests?.edges[0]?.node;

  const getIsCanShowBanner = useCallback((banner?: Banner) => {
    if (banner === Banner.Apps) {
      return Boolean(!bannerData?.viewer.hasMobileApp);
    }

    if (banner === Banner.Smart) {
      return Boolean(!bannerData?.viewer.subscriptionInfo?.endAt);
    }

    if (banner === Banner.ReviewRequest) {
      return (
        reviewRequest
        && getClosedLastReviewRequest() !== reviewRequest.id
        && reviewRequest.userRole === UserRole.Buyer
      );
    }

    return false;
  }, [bannerData, reviewRequest]);

  const getNextBanner = useCallback((showedBanners?: StorageData | null) => {
    const notShowedBanners = showedBanners
      ? BANNERS_LIST.filter((banner) => !showedBanners[banner])
      : BANNERS_LIST;
    const canBeAlreadyShowed = showedBanners
      ? Object
        .keys(showedBanners)
        .filter((banner: Banner) => {
          const lastShownDate = dayjs(showedBanners[banner]);
          const daysCountFromLastShown = dayjs.duration(dayjs().diff(lastShownDate)).asDays();
          return daysCountFromLastShown > 1;
        }) as Banner[]
      : [];

    return [
      ...notShowedBanners,
      ...canBeAlreadyShowed,
    ].find((banner) => getIsCanShowBanner(banner)) || null;
  }, [getIsCanShowBanner]);

  const handleCloseBanner = () => {
    setVisibleBanner(null);
  };

  useEffect(() => {
    const isAlreadyShown = getIsBannerShown();
    const showedBanners = getShowedBanners();

    if (
      !isAlreadyShown
      && !bannerDataLoading
      && !bannerDataV4Loading
    ) {
      const nextBanner = getNextBanner(showedBanners);
      const updatedShowedBanners = {
        ...showedBanners,
      };

      if (nextBanner) {
        updatedShowedBanners[nextBanner] = dayjs().toString();
      }
      setVisibleBanner(nextBanner);
      setShowedBanners(updatedShowedBanners);
      setIsBannerShown(true);
    }
  }, [
    getNextBanner,
    bannerDataLoading,
    bannerDataV4Loading,
  ]);

  return (
    <>
      <AnimatedAppsPromotionBanner
        placement={BannerPlacement.Main}
        isVisible={visibleBanner === Banner.Apps}
        onClose={handleCloseBanner}
      />

      {visibleBanner === Banner.Smart && (
        <AnimatedSmartBanner
          description={bannerData?.smartInfo.mainBannerText}
          availableUntilTheTime={bannerData?.smartInfo.availableUntilTheTime}
          onClose={handleCloseBanner}
        />
      )}

      {reviewRequest && visibleBanner === Banner.ReviewRequest && (
        <AnimatedReviewBanner
          reviewRequest={reviewRequest}
          onClose={() => {
            setClosedLastReviewRequest(reviewRequest.id);
            handleCloseBanner();
          }}
        />
      )}
    </>
  );
};
